import { useState, useEffect, useRef } from 'react';
import { Send, Copy, Download, RotateCcw, User, PanelLeftClose, PanelLeft, RefreshCw, ChevronRight, Trash2, MoreVertical, Code, Paperclip, X, FileText, Heart } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import ChatDisclaimer from '../../components/ChatDisclaimer';
import { streamAIResponseWithAutoContinue, exportBubbleToPDF, exportBubbleToDOCX } from '../../services/aiTextGeneratorService';

import { getAllConversations, getConversation, deleteConversation, renameConversation, setCurrentConversationId, getCurrentConversationId, clearCurrentConversation, autoSaveConversation } from '../../services/conversationHistoryService';
import ConversationHistory from '../ai-text-generator/components/ConversationHistory';
import MarkdownRenderer from '../ai-text-generator/components/MarkdownRenderer';
import { processImagesForUpload } from '../../utils/imageProcessing';

import { streamHealthImprovementResponse, processHealthImprovementDocuments, checkHealthImprovementConnection } from '../../services/healthImprovementService';

import GlobalNavigationMenu from '../../components/GlobalNavigationMenu';
import useTrialGuard from '../../hooks/useTrialGuard';
import TrialPaywall from '../../components/TrialPaywall';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function HealthImprovementPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamMessage, setCurrentStreamMessage] = useState('');
  const [apiConnected, setApiConnected] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationIdState] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const cancelControllerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const { trialBlocked, setTrialBlocked, checkoutLoading, checkoutError, setCheckoutError, checkBeforeAI, incrementAfterAI, handleAssinarPro } = useTrialGuard('health_improvement');

  // PRO plan check
  const [isPro, setIsPro] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlan = async () => {
      if (!user) return;
      try {
        const { data } = await supabase?.from('profiles')?.select('plan')?.eq('id', user?.id)?.single();
        if (data?.plan === 'pro') setIsPro(true);
      } catch {}
    };
    fetchPlan();
  }, [user]);

  const CHAT_ID = 'health_improvement';

  const followUpSuggestions = [
    "Explique de forma mais simples",
    "Dê exemplos práticos",
    "Crie um plano de ação",
    "Quais são os próximos passos?"
  ];

  const handleNavClick = (path) => {
    window.location.href = path;
  };

  useEffect(() => {
    checkConnection();
    loadConversations();
    restoreLastConversation();
  }, []);

  useEffect(() => {
    if (messages?.length > 0 && currentConversationId) {
      autoSaveConversation(currentConversationId, messages, CHAT_ID);
      loadConversations();
    }
  }, [messages, currentConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamMessage, showTypingIndicator]);

  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef?.current?.scrollHeight, 200) + 'px';
    }
  }, [input]);

  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      if (document.activeElement === textareaRef?.current) return;

      if (e?.key?.toLowerCase() === 'r' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        const lastMessage = messages?.[messages?.length - 1];
        if (lastMessage?.role === 'assistant' && !isStreaming) {
          handleRegenerateResponse(messages?.length - 1);
        }
      }

      if (e?.key?.toLowerCase() === 'c' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        const lastMessage = messages?.[messages?.length - 1];
        if (lastMessage?.role === 'assistant') {
          handleCopy(lastMessage?.content);
        }
      }

      if (e?.key === 'Delete' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        if (messages?.length > 0 && !isStreaming) {
          handleDeleteMessage(messages?.length - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  }, [messages, isStreaming]);

  const checkConnection = async () => {
    try {
      const status = await checkHealthImprovementConnection();
      setApiConnected(status?.connected);
    } catch (error) {
      setApiConnected(false);
    }
  };

  const loadConversations = () => {
    const allConversations = getAllConversations(CHAT_ID);
    setConversations(allConversations);
  };

  const restoreLastConversation = () => {
    const lastConversationId = getCurrentConversationId(CHAT_ID);
    if (lastConversationId) {
      const conversation = getConversation(lastConversationId, CHAT_ID);
      if (conversation) {
        setMessages(conversation?.messages || []);
        setCurrentConversationIdState(lastConversationId);
      }
    }
  };

  const handleSelectConversation = (conversationId) => {
    if (isStreaming) {
      cancelGeneration();
    }

    const conversation = getConversation(conversationId, CHAT_ID);
    if (conversation) {
      setMessages(conversation?.messages || []);
      setCurrentConversationIdState(conversationId);
      setCurrentConversationId(conversationId, CHAT_ID);
      setInput('');
      setCurrentStreamMessage('');
      setProgress(null);
      setShowTypingIndicator(false);
    }
  };

  const handleDeleteConversation = (conversationId) => {
    const success = deleteConversation(conversationId, CHAT_ID);
    if (success) {
      loadConversations();
      
      if (conversationId === currentConversationId) {
        handleNewChat();
      }
    }
  };

  const handleRenameConversation = (conversationId, newTitle) => {
    const success = renameConversation(conversationId, newTitle, CHAT_ID);
    if (success) {
      loadConversations();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelGeneration = () => {
    if (cancelControllerRef?.current) {
      cancelControllerRef?.current?.abort();
      setIsStreaming(false);
      setCurrentStreamMessage('');
      setProgress(null);
      setShowTypingIndicator(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (files?.length === 0) return;

    const allowedDocTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain'
    ];

    const allowedDocExtensions = ['.pdf', '.docx', '.doc', '.txt'];
    const allowedImageTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp'
    ];
    
    const allowedImageExtensions = ['.png', '.jpg', '.jpeg', '.webp'];
    
    const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
    const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024;
    const MAX_FILES = 2;

    if (uploadedFiles?.length + files?.length > MAX_FILES) {
      alert(`Você pode anexar até ${MAX_FILES} arquivos por mensagem para comparação. Você já tem ${uploadedFiles?.length} arquivo(s) anexado(s).`);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const filesToProcess = files?.slice(0, MAX_FILES - uploadedFiles?.length);
    if (files?.length > filesToProcess?.length) {
      alert(`Apenas os primeiros ${filesToProcess?.length} arquivo(s) serão anexados (limite de ${MAX_FILES} arquivos por mensagem).`);
    }

    const validFiles = [];
    const errors = [];

    for (const file of filesToProcess) {
      const fileExtension = file?.name?.substring(file?.name?.lastIndexOf('.'))?.toLowerCase();
      
      const isImage = (
        allowedImageTypes?.includes(file?.type) || 
        allowedImageExtensions?.includes(fileExtension)
      );
      
      const isValidImageFormat = isImage && (
        file?.type === 'image/png' || 
        file?.type === 'image/jpeg' || 
        file?.type === 'image/jpg' || 
        file?.type === 'image/webp' ||
        fileExtension === '.png' ||
        fileExtension === '.jpg' ||
        fileExtension === '.jpeg' ||
        fileExtension === '.webp'
      );
      
      const isDocument = (
        allowedDocTypes?.includes(file?.type) || 
        allowedDocExtensions?.includes(fileExtension)
      );
      
      const isValidDocumentFormat = isDocument && (
        file?.type === 'application/pdf' ||
        file?.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file?.type === 'application/msword' ||
        file?.type === 'text/plain' ||
        fileExtension === '.pdf' ||
        fileExtension === '.docx' ||
        fileExtension === '.doc' ||
        fileExtension === '.txt'
      );

      if (isImage) {
        if (!isValidImageFormat) {
          errors?.push(`Formato de imagem inválido: "${file?.name}". Apenas PNG, JPG, JPEG e WEBP são aceitos.`);
          continue;
        }

        if (file?.size > MAX_IMAGE_SIZE) {
          const fileSizeInMB = (file?.size / (1024 * 1024))?.toFixed(2);
          errors?.push(`A imagem "${file?.name}" é muito grande (${fileSizeInMB}MB). O tamanho máximo permitido para imagens é 5MB.`);
          continue;
        }
        
        if (file?.size === 0) {
          errors?.push(`A imagem "${file?.name}" parece estar vazia ou corrompida. Tente outro arquivo.`);
          continue;
        }

        validFiles?.push({ file, isImage: true });
      } else if (isDocument) {
        if (!isValidDocumentFormat) {
          errors?.push(`Formato de documento inválido: "${file?.name}". Apenas PDF, DOCX, DOC e TXT são aceitos.`);
          continue;
        }
        
        if (file?.size > MAX_DOCUMENT_SIZE) {
          const fileSizeInMB = (file?.size / (1024 * 1024))?.toFixed(2);
          errors?.push(`O documento "${file?.name}" é muito grande (${fileSizeInMB}MB). O tamanho máximo permitido é 50MB.`);
          continue;
        }
        
        if (file?.size === 0) {
          errors?.push(`O documento "${file?.name}" parece estar vazio ou corrompido. Tente outro arquivo.`);
          continue;
        }
        
        validFiles?.push({ file, isImage: false });
      } else {
        errors?.push(`Formato não suportado: "${file?.name}". Para imagens, use PNG, JPG, JPEG ou WEBP. Para documentos, use PDF, DOCX, DOC ou TXT.`);
      }
    }

    if (errors?.length > 0) {
      const errorMessage = errors?.length === 1 
        ? errors?.[0]
        : `Alguns arquivos não puderam ser adicionados:\n\n${errors?.map((err, idx) => `${idx + 1}. ${err}`)?.join('\n')}`;
      
      alert(errorMessage);
    }

    if (validFiles?.length === 0) {
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsProcessingFile(true);

    try {
      const processedFiles = [];
      
      const imageFiles = validFiles?.filter(f => f?.isImage);
      const documentFiles = validFiles?.filter(f => !f?.isImage);
      
      if (imageFiles?.length > 0) {
        const result = await processImagesForUpload(imageFiles?.map(f => ({
          file: f?.file,
          name: f?.file?.name,
          size: f?.file?.size,
          type: f?.file?.type
        })));
        
        result?.processedImages?.forEach(img => {
          processedFiles?.push({
            name: img?.name,
            size: img?.size,
            type: img?.type,
            content: img?.content,
            isImage: true,
            isPDF: false,
            preview: img?.preview,
            file: img?.file
          });
        });
        
        if (result?.failedImages?.length > 0) {
          const failedNames = result?.failedImages?.map(f => f?.name)?.join(', ');
          console.warn(`Imagens ignoradas devido a falha no processamento: ${failedNames}`);
          
          if (result?.processedImages?.length === 0 && documentFiles?.length === 0) {
            alert(`Não foi possível processar as imagens selecionadas. Você pode continuar com mensagem apenas de texto.`);
          }
        }
      }
      
      for (const { file } of documentFiles) {
        processedFiles?.push({
          name: file?.name,
          size: file?.size,
          type: file?.type,
          content: null,
          isImage: false,
          isPDF: file?.type === 'application/pdf' || file?.name?.endsWith('.pdf'),
          preview: null,
          file: file
        });
      }

      if (processedFiles?.length > 0) {
        setUploadedFiles(prev => [...prev, ...processedFiles]);
      }
    } catch (error) {
      console.error('Erro ao processar arquivos:', error);
      
      const errorMsg = error?.message || 'Ocorreu um erro ao processar os arquivos. Verifique se os arquivos não estão corrompidos e tente novamente.';
      alert(errorMsg);
    } finally {
      setIsProcessingFile(false);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = (fileIndex) => {
    setUploadedFiles(prev => prev?.filter((_, idx) => idx !== fileIndex));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const handleSendMessage = async () => {
    if ((!input?.trim() && uploadedFiles?.length === 0) || isStreaming) return;

    // ── TRIAL GUARD ──
    const allowed = await checkBeforeAI();
    if (!allowed) return;
    // ── END TRIAL GUARD ──

    const userMessage = input?.trim();
    const filesToProcess = [...uploadedFiles];
    
    setInput('');
    setUploadedFiles([]);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }

    const newUserMessage = {
      role: 'user',
      content: userMessage || 'Analise os arquivos anexados',
      files: filesToProcess?.length > 0 ? filesToProcess : undefined,
      timestamp: new Date()?.toISOString()
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setIsStreaming(true);
    setCurrentStreamMessage('');
    setShowTypingIndicator(true);

    try {
      cancelControllerRef.current = new AbortController();

      let responseContent = '';
      let modelUsed = 'gpt-4o';

      if (filesToProcess?.length > 0) {
        // Hybrid file processing: gpt-4o-mini for indexing, gpt-4o for response
        const result = await processHealthImprovementDocuments(
          filesToProcess,
          userMessage || 'Analise os arquivos anexados e forneça insights sobre saúde',
          currentConversationId || `health_improvement_${Date.now()}`,
          (chunk) => {
            setCurrentStreamMessage(prev => prev + chunk);
          },
          (progressInfo) => {
            setProgress(progressInfo);
          },
          cancelControllerRef?.current?.signal
        );

        responseContent = result?.content;
        modelUsed = result?.model;
      } else {
        // Text-only response using gpt-4o
        const conversationMessages = updatedMessages?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        }));

        const result = await streamHealthImprovementResponse(
          conversationMessages,
          (chunk) => {
            setCurrentStreamMessage(prev => prev + chunk);
          },
          (progressInfo) => {
            setProgress(progressInfo);
          },
          cancelControllerRef?.current?.signal
        );

        responseContent = result?.content;
        modelUsed = result?.model;
      }

      const assistantMessage = {
        role: 'assistant',
        content: responseContent,
        model: modelUsed,
        timestamp: new Date()?.toISOString()
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      setCurrentStreamMessage('');
      setShowTypingIndicator(false);
      // ── INCREMENT USAGE ──
      await incrementAfterAI();
      // ── END INCREMENT ──

      if (!currentConversationId) {
        const newConversationId = `health_improvement_${Date.now()}`;
        setCurrentConversationIdState(newConversationId);
        setCurrentConversationId(newConversationId, CHAT_ID);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      if (error?.message !== 'Geração cancelada pelo usuário' && error?.message !== 'Análise cancelada pelo usuário') {
        const errorMessage = {
          role: 'assistant',
          content: `❌ Erro: ${error?.message || 'Não foi possível gerar a resposta. Tente novamente.'}`,
          isError: true,
          timestamp: new Date()?.toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      
      setCurrentStreamMessage('');
      setShowTypingIndicator(false);
    } finally {
      setIsStreaming(false);
      setProgress(null);
      cancelControllerRef.current = null;
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator?.clipboard?.writeText(text);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleNewChat = () => {
    if (isStreaming) {
      cancelGeneration();
    }
    setMessages([]);
    setInput('');
    setCurrentStreamMessage('');
    setProgress(null);
    setShowTypingIndicator(false);
    setCurrentConversationIdState(null);
    clearCurrentConversation(CHAT_ID);
  };

  const handleSend = (messageText) => {
    if (messageText) {
      setInput(messageText);
      setTimeout(() => {
        handleSendMessage();
      }, 0);
    } else {
      handleSendMessage();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFollowUpClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleRegenerateResponse = async (messageIndex) => {
    if (isStreaming) return;

    const messagesUpToPrompt = messages?.slice(0, messageIndex);
    
    setMessages(messagesUpToPrompt);

    const lastUserMessage = messagesUpToPrompt?.filter(m => m?.role === 'user')?.pop();
    
    if (!lastUserMessage) return;

    try {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setCurrentStreamMessage('');
      
      cancelControllerRef.current = new AbortController();
      
      let fullResponse = '';
      
      const hasVisionContent = lastUserMessage?.images && lastUserMessage?.images?.length > 0;
      
      const conversationMessages = messagesUpToPrompt?.map(msg => ({
        role: msg?.role,
        content: msg?.content
      }));
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowTypingIndicator(false);
      
      const result = await streamAIResponseWithAutoContinue(
        conversationMessages,
        {},
        (chunk) => {
          fullResponse += chunk;
          setCurrentStreamMessage(fullResponse);
        },
        (progressInfo) => {
          setProgress(progressInfo);
        },
        cancelControllerRef?.current?.signal,
        hasVisionContent
      );
      
      const updatedMessages = [...messagesUpToPrompt, { 
        role: 'assistant', 
        content: result?.content, 
        timestamp: new Date(),
        model: result?.model,
        cycles: result?.totalCycles,
        limitReached: result?.limitReached
      }];
      
      setMessages(updatedMessages);
      setCurrentStreamMessage('');
      setProgress(null);
      
    } catch (error) {
      setShowTypingIndicator(false);
      if (error?.message === 'Geração cancelada pelo usuário') {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: currentStreamMessage || 'Resposta cancelada pelo usuário.', 
          timestamp: new Date(),
          isCancelled: true
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: error?.message || 'Erro ao gerar resposta. Tente novamente.',
          timestamp: new Date(),
          isError: true
        }]);
      }
      setCurrentStreamMessage('');
      setProgress(null);
    } finally {
      setIsStreaming(false);
      cancelControllerRef.current = null;
    }
  };

  const handleContinueResponse = async (messageIndex) => {
    if (isStreaming) return;

    const messageToExtend = messages?.[messageIndex];
    if (messageToExtend?.role !== 'assistant') return;

    const messagesUpToThis = messages?.slice(0, messageIndex + 1);
    
    try {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setCurrentStreamMessage('');
      
      cancelControllerRef.current = new AbortController();
      
      let fullResponse = messageToExtend?.content;
      
      const lastUserMessage = messagesUpToThis?.filter(m => m?.role === 'user')?.pop();
      const hasVisionContent = lastUserMessage?.images && lastUserMessage?.images?.length > 0;
      
      const conversationMessages = [
        ...messagesUpToThis?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        })),
        {
          role: 'user',
          content: 'Continue de onde parou, expandindo a resposta anterior.'
        }
      ];
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowTypingIndicator(false);
      
      const result = await streamAIResponseWithAutoContinue(
        conversationMessages,
        {},
        (chunk) => {
          fullResponse += chunk;
          setCurrentStreamMessage(fullResponse);
        },
        (progressInfo) => {
          setProgress(progressInfo);
        },
        cancelControllerRef?.current?.signal,
        hasVisionContent
      );
      
      const updatedMessages = messages?.map((msg, idx) => 
        idx === messageIndex 
          ? {
              ...msg,
              content: result?.content,
              model: result?.model,
              cycles: (msg?.cycles || 0) + 1,
              limitReached: false
            }
          : msg
      );
      
      setMessages(updatedMessages);
      setCurrentStreamMessage('');
      setProgress(null);
      
    } catch (error) {
      setShowTypingIndicator(false);
      setCurrentStreamMessage('');
      setProgress(null);
    } finally {
      setIsStreaming(false);
      cancelControllerRef.current = null;
    }
  };

  const handleDeleteMessage = (messageIndex) => {
    if (isStreaming) return;
    
    const messageToDelete = messages?.[messageIndex];
    if (messageToDelete?.role === 'user' && messages?.[messageIndex + 1]?.role === 'assistant') {
      setMessages(messages?.filter((_, idx) => idx !== messageIndex && idx !== messageIndex + 1));
    } else {
      setMessages(messages?.filter((_, idx) => idx !== messageIndex));
    }
  };

  const handleCopyCode = (content) => {
    const codeBlockRegex = /```[\s\S]*?```/g;
    const codeBlocks = content?.match(codeBlockRegex);
    
    if (codeBlocks && codeBlocks?.length > 0) {
      const code = codeBlocks?.map(block => 
        block?.replace(/```[\w]*\n?/, '')?.replace(/```$/, '')
      )?.join('\n\n');
      handleCopy(code);
    } else {
      handleCopy(content);
    }
  };

  return (
    <div id="health-improvement">
      <div className="h-screen flex" style={{ backgroundColor: '#F8FAFC' }}>
        {/* Sidebar - Conversation History */}
        <div className={`flex-none border-r border-gray-200 transition-all duration-300 ${
          showSidebar ? 'w-64' : 'w-0'
        } overflow-hidden`} style={{ backgroundColor: '#F8FAFC' }}>
          <div className="h-full flex flex-col">
            <div className="flex-none p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold" style={{ color: '#0F172A' }}>Conversas</h2>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Fechar"
                >
                  <PanelLeftClose size={18} style={{ color: '#0B1220' }} />
                </button>
              </div>
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 hover:opacity-90 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#FB7185' }}
              >
                <Icon name="Plus" size={16} />
                <span className="text-sm font-medium">Nova Conversa</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <ConversationHistory
                conversations={conversations}
                currentConversationId={currentConversationId}
                onSelectConversation={handleSelectConversation}
                onRenameConversation={handleRenameConversation}
                onDeleteConversation={handleDeleteConversation}
              />
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex-none border-b border-gray-200" style={{ backgroundColor: '#F8FAFC' }}>
            <div className="w-full max-w-[860px] mx-auto flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                {!showSidebar && (
                  <button
                    onClick={() => setShowSidebar(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Mostrar histórico"
                  >
                    <PanelLeft size={20} style={{ color: '#0B1220' }} />
                  </button>
                )}
                <GlobalNavigationMenu />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)' }}>
                    <Heart size={18} className="text-white" />
                  </div>
                  <h1 className="text-lg font-semibold" style={{ color: '#0F172A' }}>Melhore sua Saúde</h1>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {messages?.length > 0 && (
                  <button
                    onClick={handleNewChat}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RotateCcw size={16} style={{ color: '#0B1220' }} />
                    <span className="text-sm font-medium hidden sm:inline" style={{ color: '#0B1220' }}>Nova conversa</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages Container - Scrollable */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto"
            style={{ backgroundColor: '#F8FAFC' }}
          >
            <div className="w-full max-w-[860px] mx-auto px-4 py-6">
              {messages?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full space-y-6 py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)' }}>
                    <Heart size={32} className="text-white" />
                  </div>
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Como posso ajudar a melhorar sua saúde hoje?</h2>
                    <p style={{ color: '#0B1220' }}>Assistente inteligente para melhorar sua saúde física, mental e emocional. ATENÇÃO: NENHUM ASSISTENTE ONLINE PODE SUBSTITUIR UM(A) MÉDICO(A) PORTANTO NOSSAS DICAS SÃO APENAS CONSELHOS BASEADOS NA CIÊNCIA!</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {messages?.map((message, index) => (
                    <div
                      key={index}
                      className={`group flex gap-3 ${
                        message?.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message?.role === 'assistant' && (
                        <div className="flex-none">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)' }}>
                            <Heart size={16} className="text-white" />
                          </div>
                        </div>
                      )}
                      
                      <div className={`flex flex-col gap-2 max-w-[85%] ${
                        message?.role === 'user' ? 'items-end' : 'items-start'
                      }`}>
                        <div
                          className={`px-4 py-3 rounded-2xl ${
                            message?.role === 'user' ?'text-white' :'bg-white text-gray-900 shadow-sm'
                          }`}
                          style={message?.role === 'user' ? { backgroundColor: '#FB7185' } : {}}
                        >
                          {message?.images && message?.images?.length > 0 && (
                            <div className="mb-3 space-y-2">
                              {message?.images?.map((img, imgIdx) => (
                                <div key={imgIdx} className="space-y-1">
                                  {img?.label && (
                                    <div className="text-xs font-semibold opacity-90">{img?.label}</div>
                                  )}
                                  <img
                                    src={img?.preview}
                                    alt={img?.name}
                                    className="max-w-full rounded-lg"
                                    style={{ maxHeight: '300px' }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {message?.documents && message?.documents?.length > 0 && (
                            <div className="mb-3 space-y-2">
                              {message?.documents?.map((doc, docIdx) => (
                                <div key={docIdx} className="flex items-center gap-2 text-sm opacity-90">
                                  {doc?.label && (
                                    <span className="font-semibold">{doc?.label}:</span>
                                  )}
                                  <FileText size={14} />
                                  <span>{doc?.name}</span>
                                  <span className="text-xs">({formatFileSize(doc?.size)})</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {message?.role === 'assistant' ? (
                            <MarkdownRenderer content={message?.content} />
                          ) : (
                            <div className="whitespace-pre-wrap">{message?.content}</div>
                          )}
                        </div>
                        
                        {message?.role === 'assistant' && (
                          <div className="flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                            {message?.apiType && (
                              <span className="px-2 py-1 bg-gray-100 rounded">
                                {message?.apiType === 'assistants' ? 'Assistants API' : 'Chat API'}
                              </span>
                            )}
                            {message?.cycles && (
                              <span className="px-2 py-1 bg-gray-100 rounded">{message?.cycles} ciclo(s)</span>
                            )}
                            {message?.tokensUsed && (
                              <span className="px-2 py-1 bg-gray-100 rounded">{message?.tokensUsed} tokens</span>
                            )}
                          </div>
                        )}
                        
                        {message?.role === 'assistant' && !message?.isError && !message?.isCancelled && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleRegenerateResponse(index)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Regenerar (R)"
                              disabled={isStreaming}
                            >
                              <RefreshCw size={14} style={{ color: '#64748B' }} />
                            </button>
                            <button
                              onClick={() => handleContinueResponse(index)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Continuar"
                              disabled={isStreaming}
                            >
                              <ChevronRight size={14} style={{ color: '#64748B' }} />
                            </button>
                            <button
                              onClick={() => handleCopyCode(message?.content)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Copiar código"
                            >
                              <Code size={14} style={{ color: '#64748B' }} />
                            </button>
                            <button
                              onClick={() => handleCopy(message?.content)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Copiar (C)"
                            >
                              <Copy size={14} style={{ color: '#64748B' }} />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(index)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Deletar (Delete)"
                              disabled={isStreaming}
                            >
                              <Trash2 size={14} style={{ color: '#64748B' }} />
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => setShowMobileMenu(showMobileMenu === index ? null : index)}
                                className="p-1.5 hover:bg-gray-100 rounded transition-colors md:hidden"
                                title="Mais opções"
                              >
                                <MoreVertical size={14} style={{ color: '#64748B' }} />
                              </button>
                              {showMobileMenu === index && (
                                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]">
                                  <button
                                    onClick={() => {
                                      exportBubbleToPDF(message?.content, `resposta-${index + 1}`);
                                      setShowMobileMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm"
                                  >
                                    <Download size={14} />
                                    <span>Exportar PDF</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      exportBubbleToDOCX(message?.content, `resposta-${index + 1}`);
                                      setShowMobileMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm"
                                  >
                                    <Download size={14} />
                                    <span>Exportar DOCX</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {message?.role === 'user' && (
                        <div className="flex-none">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E2E8F0' }}>
                            <User size={16} style={{ color: '#64748B' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {showTypingIndicator && (
                    <div className="flex gap-3 justify-start">
                      <div className="flex-none">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)' }}>
                          <Heart size={16} className="text-white" />
                        </div>
                      </div>
                      <div className="px-4 py-3 rounded-2xl bg-white shadow-sm">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {currentStreamMessage && (
                    <div className="flex gap-3 justify-start">
                      <div className="flex-none">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)' }}>
                          <Heart size={16} className="text-white" />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 max-w-[85%]">
                        <div className="px-4 py-3 rounded-2xl bg-white text-gray-900 shadow-sm">
                          <MarkdownRenderer content={currentStreamMessage} />
                          <span className="inline-block w-1 h-4 bg-gray-400 animate-pulse ml-1"></span>
                        </div>
                        {progress && (
                          <div className="flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                            <span className="px-2 py-1 bg-gray-100 rounded">A resposta está sendo gerada...</span>
                            {progress?.fileName && (
                              <span className="px-2 py-1 bg-gray-100 rounded truncate max-w-[200px]">{progress?.fileName}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {messages?.length > 0 && messages?.[messages?.length - 1]?.role === 'assistant' && !isStreaming && (
                    <div className="flex flex-wrap gap-2 justify-start pl-11">
                      {followUpSuggestions?.map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleFollowUpClick(suggestion)}
                          className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          style={{ color: '#0B1220' }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          {/* Input Area - Fixed at bottom */}
          <div className="flex-none border-t border-gray-200" style={{ backgroundColor: '#F8FAFC' }}>
            <div className="w-full max-w-[860px] mx-auto px-4 py-4">
              {uploadedFiles?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {uploadedFiles?.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                      {file?.isImage ? (
                        <img src={file?.preview} alt={file?.name} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <FileText size={20} style={{ color: '#64748B' }} />
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium" style={{ color: '#0B1220' }}>{file?.name}</span>
                        <span className="text-xs" style={{ color: '#64748B' }}>{formatFileSize(file?.size)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(idx)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        <X size={16} style={{ color: '#64748B' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-end gap-3 bg-white rounded-2xl p-3 border border-gray-200">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isStreaming || isProcessingFile || uploadedFiles?.length >= 2}
                />
                <button
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={isStreaming || isProcessingFile || uploadedFiles?.length >= 2}
                  className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                    isStreaming || isProcessingFile || uploadedFiles?.length >= 2
                      ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-50 border border-gray-300'
                  }`}
                  title={uploadedFiles?.length >= 2 
                    ? "Limite de 2 arquivos atingido" 
                    : "Anexar até 2 arquivos — PDF/DOCX/TXT (até 50 MB cada) ou imagem PNG/JPG/WEBP (até 5 MB cada)"}
                >
                  {isProcessingFile ? (
                    <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#9CA3AF' }} />
                  ) : (
                    <Paperclip size={18} style={{ color: uploadedFiles?.length >= 2 ? '#9CA3AF' : '#0B1220' }} />
                  )}
                </button>

                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Como posso ajudar?"
                  className="flex-1 bg-transparent border-none outline-none resize-none text-sm max-h-[200px]"
                  style={{ color: '#0B1220' }}
                  rows={1}
                  disabled={isStreaming}
                />
                {isStreaming ? (
                  <button
                    onClick={cancelGeneration}
                    className="p-2.5 rounded-xl transition-colors flex-shrink-0"
                    style={{ backgroundColor: '#EF4444' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                  >
                    <div className="w-4 h-4 border-2 border-white"></div>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend()}
                    disabled={!input?.trim() && uploadedFiles?.length === 0}
                    className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                      input?.trim() || uploadedFiles?.length > 0
                        ? '' : 'cursor-not-allowed'
                    }`}
                    style={{
                      backgroundColor: (input?.trim() || uploadedFiles?.length > 0) ? '#FB7185' : '#E5E7EB',
                      color: (input?.trim() || uploadedFiles?.length > 0) ? '#FFFFFF' : '#9CA3AF'
                    }}
                    onMouseEnter={(e) => {
                      if (input?.trim() || uploadedFiles?.length > 0) {
                        e.currentTarget.style.backgroundColor = '#F43F5E';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (input?.trim() || uploadedFiles?.length > 0) {
                        e.currentTarget.style.backgroundColor = '#FB7185';
                      }
                    }}
                  >
                    <Send size={18} />
                  </button>
                )}
              </div>
            </div>
            <ChatDisclaimer />
          </div>
        </div>
        {trialBlocked && !isPro && (
          <TrialPaywall
            onAssinarPro={handleAssinarPro}
            checkoutLoading={checkoutLoading}
            checkoutError={checkoutError}
            onDismissError={() => setCheckoutError(null)}
            onClose={() => setTrialBlocked(false)}
          />
        )}
      </div>
    </div>
  );
}